import React, { useState, useEffect, useCallback } from "react";
import { setToken, setUnauthorizedHandler } from "./api";
import { api } from "./api";
import { GUIDELINES_ENABLED } from "./config";
import { session, workGuard, netLoading, uiSwitch } from "./helpers";
import { Icon, Modal, ToastCtx, ToastHost } from "./components/ui";
import { SwitchSpinner } from "./components/SwitchSpinner";
import { Sidebar } from "./components/Sidebar";
import { Header } from "./components/Header";
import { UnreadPosts } from "./components/Posts";
import { Login } from "./auth/Login";
import { RolePicker } from "./auth/RolePicker";
import { FaqViewer } from "./faq/FaqViewer";
import { UserManagement } from "./roles/superadmin/UserManagement";
import { DataManagement } from "./roles/superadmin/DataManagement";
import { AuditLog } from "./roles/superadmin/AuditLog";
import { ArchiveCenter } from "./roles/superadmin/ArchiveCenter";
import { Productivity } from "./roles/superadmin/Productivity";
import { FTRReport } from "./roles/superadmin/FTRReport";
import { Sessions } from "./roles/superadmin/Sessions";
import { Allocations } from "./roles/superadmin/Allocations";
import { ReviewedAging } from "./roles/superadmin/ReviewedAging";
import { RecordJourney } from "./roles/superadmin/RecordJourney";
import { OutstandingReport } from "./roles/superadmin/OutstandingReport";
import { Summary } from "./roles/superadmin/Summary";
import { ClientReport } from "./roles/superadmin/ClientReport";
import { AuditorView } from "./roles/auditor/AuditorView";
import { AdminHome } from "./roles/admin/AdminHome";
import { Dashboard } from "./roles/admin/Dashboard";
import { GroupInventory } from "./roles/admin/GroupInventory";
import { ImportCenter } from "./roles/admin/ImportCenter";
import { Historical } from "./roles/admin/Historical";
import AllRecords from "./roles/admin/AllRecords";
import { Approved } from "./roles/admin/Approved";
import GuidelineLookup from "./roles/admin/GuidelineLookup";
import { HelpQueue } from "./roles/admin/HelpQueue";
import { UserHome } from "./roles/user/UserHome";
import { UserDashboard } from "./roles/user/UserDashboard";
import { AssignedRecords } from "./roles/user/AssignedRecords";
import { MyWork } from "./roles/user/MyWork";
import { MyProductivity } from "./roles/user/MyProductivity";

/* Inventory nav: two groups (LACN, CCCMG); each expands to the four processes.
   A leaf key is inv_<GROUP>_<SHEET>, e.g. inv_LACN_END_PROCESS. */
const INV_PROCESSES = [
  ["SUBMISSION", "Submission"],
  ["END_PROCESS", "End Process"],
  ["MODIFICATION", "Modification"],
  ["MORE_INFO", "More Info Needed"],
];
const invGroupNode = (g) => ({ key: "invg_" + g, label: g, children: INV_PROCESSES.map(([s, l]) => ({ key: `inv_${g}_${s}`, label: l })) });
/* Inventory children scoped to the groups this user may see (LACN/CCCMG). */
const invChildren = (groups) => groups.map(invGroupNode);
/* The client groups a user can access: super admin → both; an admin → only the LACN/CCCMG group(s) they own. */
function invGroupsFor(role, user) {
  const ALL = ["LACN", "CCCMG"];   // Surgical is a sub-tab inside each group, not a top-level group
  if (role === "SUPER_ADMIN") return ALL;
  const mine = (user.groups || []).filter((g) => ALL.includes(g));
  return mine.length ? mine : ALL;   // an admin with no group set still sees both (safety)
}
/* Render the group→process view for a leaf key inv_<GROUP>_<SHEET>. Remounts (key) so it opens fresh. */
function renderInventory(key, user) {
  const rest = key.slice(4);
  const i = rest.indexOf("_");
  const g = rest.slice(0, i);
  const sh = rest.slice(i + 1);
  return <GroupInventory key={key} user={user} sheet={sh} group={g} />;
}

/* Each role gets its own workspace: nav items + views, structured by user type.
   Add future modules under the matching role. */
function workspaceFor(role, user, activeProcess, activeClient) {
  if (role === "SUPER_ADMIN") {
    return {
      nav: [
        { key: "dashboard", label: "Dashboard", icon: Icon.home },
        { key: "import", label: "Import", icon: Icon.clipboard },
        { key: "inventory", label: "Inventory", icon: Icon.clipboard, children: invChildren(invGroupsFor("SUPER_ADMIN", user)) },
        { key: "historical", label: "Historical", icon: Icon.history },
        { key: "analytics", label: "Analytics", icon: Icon.filter, children: [
          // All records / Approved removed from the menu — all records are visible in Inventory, and
          // approved records are reachable via the status filter in Historical.
          { key: "productivity", label: "Productivity" },
          { key: "allocations", label: "Allocations" },
          { key: "reviewedaging", label: "Reviewed aging" },
          { key: "ftr", label: "FTR / STR / TTR" },
          { key: "journey", label: "Record journey" },
          { key: "outstanding", label: "Audit sampling" },
          { key: "summary", label: "Summary" },
          { key: "clientreport", label: "Client report" },
          ...(GUIDELINES_ENABLED ? [{ key: "guidelinelookup", label: "Guideline lookup" }] : []),
        ] },
        { key: "users", label: "User management", icon: Icon.users },
        { key: "data", label: "Data management", icon: Icon.clipboard },
        { key: "archive", label: "Archive", icon: Icon.shield },
        { key: "help", label: "Help requests", icon: Icon.help },
        { key: "sessions", label: "Active sessions", icon: Icon.users },
        { key: "audit", label: "Audit log", icon: Icon.shield },
      ],
      def: "dashboard",
      titles: {},
      render: (key) =>
        key.startsWith("inv_") ? renderInventory(key, user)
        : key === "import" ? <ImportCenter user={user} />
        : key === "historical" ? <Historical />
        : key === "allrecords" ? <AllRecords />
        : key === "approved" ? <Approved />
        : key === "productivity" ? <Productivity />
        : key === "ftr" ? <FTRReport />
        : key === "users" ? <UserManagement />
        : key === "data" ? <DataManagement />
        : key === "archive" ? <ArchiveCenter />
        : key === "help" ? <HelpQueue role={user.role} />
        : key === "allocations" ? <Allocations />
        : key === "reviewedaging" ? <ReviewedAging />
        : key === "journey" ? <RecordJourney />
        : key === "outstanding" ? <OutstandingReport />
        : key === "summary" ? <Summary />
        : key === "clientreport" ? <ClientReport />
        : key === "guidelinelookup" ? <GuidelineLookup />
        : key === "sessions" ? <Sessions />
        : key === "audit" ? <AuditLog />
        : <Dashboard user={user} />,
    };
  }
  if (role === "AUDITOR") {
    return {
      nav: [{ key: "audit", label: "Records to audit", icon: Icon.clipboard }],
      def: "audit",
      titles: { audit: ["Audit", "Review the records sent to you"] },
      render: () => <AuditorView user={user} />,
    };
  }
  if (role === "CLIENT_ADMIN") {
    return {
      nav: [{ key: "home", label: "Home", icon: Icon.home }],
      def: "home",
      titles: { home: ["Client Admin", "Your workspace"] },
      render: () => (
        <div className="placeholder-home">
          <div className="ph-card">
            <div className="ph-emoji">👋</div>
            <h2>Welcome, {user.firstName}</h2>
            <p>Your <b>Client Admin</b> workspace is being set up. Features for this role are coming soon.</p>
          </div>
        </div>
      ),
    };
  }
  if (role === "SUPERVISOR") {
    return {
      nav: [
        { key: "dashboard", label: "Dashboard", icon: Icon.home },
        { key: "help", label: "Help requests", icon: Icon.help },
      ],
      def: "help",
      titles: {},
      render: (key) => (key === "help" ? <HelpQueue role={user.role} /> : <Dashboard user={user} />),
    };
  }
  if (role === "ADMIN") {
    return {
      nav: [
        { key: "dashboard", label: "Dashboard", icon: Icon.home },
        { key: "import", label: "Import", icon: Icon.clipboard },
        { key: "inventory", label: "Inventory", icon: Icon.clipboard, children: invChildren(invGroupsFor("ADMIN", user)) },
        { key: "historical", label: "Historical", icon: Icon.history },
        { key: "analytics", label: "Analytics", icon: Icon.filter, children: [
          // All records / Approved removed from the menu — all records are visible in Inventory, and
          // approved records are reachable via the status filter in Historical.
          { key: "productivity", label: "Productivity" },
          { key: "allocations", label: "Allocations" },
          { key: "reviewedaging", label: "Reviewed aging" },
          { key: "ftr", label: "FTR / STR / TTR" },
          { key: "journey", label: "Record journey" },
          { key: "outstanding", label: "Audit sampling" },
          { key: "summary", label: "Summary" },
          { key: "clientreport", label: "Client report" },
          ...(GUIDELINES_ENABLED ? [{ key: "guidelinelookup", label: "Guideline lookup" }] : []),
        ] },
        { key: "users", label: "User management", icon: Icon.users },
        { key: "data", label: "Data management", icon: Icon.clipboard },
        { key: "help", label: "Help requests", icon: Icon.help },
      ],
      def: "dashboard",
      titles: {
        dashboard: ["Dashboard", "Overview of your prior-auth work"],
        users: ["User management", "Add and manage user accounts"],
        historical: ["Historical", "All records, all statuses"],
        data: ["Data management", "Groups, locations, portals, payors and orders"],
        help: ["Help requests", "Records where a user needs help"],
      },
      render: (key) =>
        key.startsWith("inv_") ? renderInventory(key, user)
        : key === "import" ? <ImportCenter user={user} />
        : key === "historical" ? <Historical />
        : key === "allrecords" ? <AllRecords />
        : key === "approved" ? <Approved />
        : key === "productivity" ? <Productivity />
        : key === "ftr" ? <FTRReport />
        : key === "users" ? <UserManagement />
        : key === "data" ? <DataManagement />
        : key === "allocations" ? <Allocations />
        : key === "reviewedaging" ? <ReviewedAging />
        : key === "journey" ? <RecordJourney />
        : key === "outstanding" ? <OutstandingReport />
        : key === "summary" ? <Summary />
        : key === "clientreport" ? <ClientReport />
        : key === "guidelinelookup" ? <GuidelineLookup />
        : key === "help" ? <HelpQueue role={user.role} />
        : <Dashboard user={user} />,
    };
  }
  return {
    nav: [
      { key: "records", label: "My records", icon: Icon.clipboard },
      { key: "worked", label: "My work", icon: Icon.history },
      { key: "myprod", label: "My productivity", icon: Icon.filter },
    ],
    def: "records",
    titles: {
      records: ["My records", "Records assigned to you"],
    },
    render: (key) =>
      key === "worked" ? <MyWork />
      : key === "myprod" ? <MyProductivity user={user} />
      : <AssignedRecords user={user} process={null} processes={user.processes || []} client={activeClient} />,
  };
}

export default function App() {
  const [auth, setAuth] = useState(() => {
    const a = session.load();
    if (a?.token) setToken(a.token);   // restore the token synchronously, before any screen fires an API call
    return a;
  });
  const [active, setActive] = useState(null);
  // App is the SOLE renderer of the centered "tab switched" overlay. Views fire uiSwitch.trigger() (synchronous,
  // so the spinner shows the instant a tab is clicked) but never render their own overlay — that was the second,
  // offset spinner. uiSwitch keeps the overlay up for the trigger window even when several fire in quick succession.
  const [switching, setSwitching] = useState(false);
  useEffect(() => uiSwitch.sub(setSwitching), []);
  const triggerSwitch = () => uiSwitch.trigger();
  // Data-driven loader: stays up as long as ANY request is in flight (so slow views — Historical, My work,
  // productivity — keep the spinner until their data actually arrives). Small delay avoids flicker on fast calls.
  const [netBusy, setNetBusy] = useState(false);
  useEffect(() => {
    let timer = null;
    const unsub = netLoading.sub((n) => {
      if (n > 0) { if (!timer) timer = window.setTimeout(() => { setNetBusy(true); timer = null; }, 150); }
      else { if (timer) { clearTimeout(timer); timer = null; } setNetBusy(false); }
    });
    return () => { if (timer) clearTimeout(timer); unsub(); };
  }, []);
  const [faqOpen, setFaqOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [archiveCount, setArchiveCount] = useState(0);
  useEffect(() => {
    const role = auth?.activeRole || auth?.user?.role;
    if (role !== "SUPER_ADMIN" && role !== "ADMIN") return;
    let alive = true;
    // first load is loud (shows the overlay like any navigation); the 60s poll is silent.
    const load = (silent) => api.listArchiveRequests(silent ? { silent: true } : undefined)
      .then((r) => { if (alive) setArchiveCount(Array.isArray(r) ? r.length : 0); }).catch(() => {});
    load(); const t = setInterval(() => load(true), 60000);
    return () => { alive = false; clearInterval(t); };
  }, [auth]);
  const [toasts, setToasts] = useState([]);
  const [switchAsk, setSwitchAsk] = useState(false);
  const [switchBusy, setSwitchBusy] = useState(false);
  const [navAsk, setNavAsk] = useState(null);      // target tab key pending an unsaved-changes confirm
  const [navBusy, setNavBusy] = useState(false);

  // (token is restored synchronously in the auth initializer above)

  const pushToast = useCallback((msg, kind = "info") => {
    const id = Math.random();
    setToasts((t) => [...t, { id, msg, kind }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 3200);
  }, []);

  // Sidebar navigation while a record is open (a WorkView keeps workGuard.current set):
  //  • a kept-in-Reviewed record still needing today's note (guard.block) → hard-stop, must Save & close first
  //  • a process record with entries typed but not saved (guard.dirty) → ask: Save & switch / Switch without saving
  //  • an untouched record → switch freely, no prompt.
  const navigate = useCallback((key) => {
    const g = workGuard.current;
    if (g && g.block) {
      pushToast(g.message || "Add today's note, then Save & close before leaving this record.", "err");
      return;
    }
    if (g && g.dirty) { setNavAsk(key); return; }
    workGuard.current = null;
    if (key !== active) triggerSwitch();
    setActive(key);
  }, [pushToast, active]);
  const doNav = (key) => { workGuard.current = null; setNavAsk(null); if (key !== active) triggerSwitch(); setActive(key); };
  const saveThenNav = async () => {
    const key = navAsk;
    setNavBusy(true);
    try { if (workGuard.current && workGuard.current.save) { const ok = await workGuard.current.save(); if (ok === false) { setNavBusy(false); return; } } }
    catch { setNavBusy(false); return; }
    setNavBusy(false); doNav(key);
  };

  const onLogin = (res, remember) => {
    setToken(res.token);
    const rec = { token: res.token, user: res.user };
    session.save(rec, remember);
    setAuth(rec);
    setActive(null);
  };
  const onPickRole = (role, process, client) => {
    const rec = { ...auth, activeRole: role, activeProcess: process, activeClient: client || null };
    session.save(rec, true);
    setAuth(rec);
    setActive(null);
  };
  const onLogout = () => {
    // Same hard-stop the sidebar uses: a kept-in-Reviewed record still needing today's note can't be
    // abandoned by logging out — the user must add the note and Save & close first.
    const g = workGuard.current;
    if (g && g.block) { pushToast(g.message || "Add today's note, then Save & close before logging out.", "err"); return; }
    session.clear(); setToken(null); setAuth(null);
  };

  // If a session is revoked (or expires), the server returns 401 — log out and show a notice.
  useEffect(() => {
    setUnauthorizedHandler(() => {
      session.clear(); setToken(null); setAuth(null); setActive(null);
      pushToast("You were signed out. Please log in again.", "info");
    });
  }, [pushToast]);

  // switch client/process without a full logout — re-opens the picker
  const doSwitch = () => {
    workGuard.current = null;
    const rec = { ...auth, activeRole: null, activeProcess: null, activeClient: null };
    session.save(rec, true);
    setAuth(rec);
    setActive(null);
  };
  const onSwitch = () => {
    const g = workGuard.current;
    if (g && g.block) { pushToast(g.message || "Add today's note, then Save & close before leaving this record.", "err"); return; }
    if (g && g.dirty) setSwitchAsk(true);
    else doSwitch();
  };
  const saveThenSwitch = async () => {
    setSwitchBusy(true);
    try { if (workGuard.current && workGuard.current.save) { const ok = await workGuard.current.save(); if (ok === false) { setSwitchBusy(false); return; } } }
    catch { /* keep them here if the save failed */ setSwitchBusy(false); return; }
    setSwitchBusy(false); setSwitchAsk(false); doSwitch();
  };

  if (!auth) {
    return (
      <ToastCtx.Provider value={pushToast}>
        <Login onLogin={onLogin} />
        <ToastHost toasts={toasts} />
      </ToastCtx.Provider>
    );
  }

  // decide whether the post-login picker is still needed
  const roles = auth.user.roles && auth.user.roles.length ? auth.user.roles : [auth.user.role];
  const procs = auth.user.processes || [];
  const clients = auth.user.groups || [];
  const needsPicker = !auth.activeRole && (
    roles.length > 1 || clients.length > 1
  );
  if (needsPicker) {
    return (
      <ToastCtx.Provider value={pushToast}>
        <RolePicker user={auth.user} onPick={onPickRole} />
        <ToastHost toasts={toasts} />
      </ToastCtx.Provider>
    );
  }

  const ws = workspaceFor(auth.activeRole || auth.user.role, auth.user, auth.activeProcess, auth.activeClient);
  ws.nav.forEach((n) => { if (n.key === "archive") n.count = archiveCount; });
  const collectKeys = (items) => items.flatMap((n) => (n.children ? collectKeys(n.children) : [n.key]));
  const validKeys = collectKeys(ws.nav);
  const activeKey = active && validKeys.includes(active) ? active : ws.def;
  const [title, subtitle] = ws.titles[activeKey] || ["", ""];
  const canSwitch = roles.length > 1 || clients.length > 1 || procs.length > 1;

  return (
    <ToastCtx.Provider value={pushToast}>
      <div className={"app-frame" + (collapsed ? " collapsed" : "")}>
        <Header title={title} subtitle={subtitle} onOpenFaq={() => setFaqOpen(true)} onLogout={onLogout}
          collapsed={collapsed} onToggle={() => setCollapsed((c) => !c)} user={auth.user} />
        <div className="shell">
          <Sidebar user={auth.user} nav={ws.nav} active={activeKey} onNavigate={navigate} collapsed={collapsed}
            client={auth.activeClient} process={auth.activeProcess} canSwitch={canSwitch} onSwitch={onSwitch} />
          <div className="main">
            <div className="content">{(switching || netBusy) && <div className="switch-overlay"><SwitchSpinner /></div>}{ws.render(activeKey)}</div>
          </div>
        </div>
      </div>

      {switchAsk && (
        <Modal title="Save your work first?" sub="You have unsaved changes on the record you're working on"
          onClose={() => setSwitchAsk(false)}
          foot={<>
            <button className="btn btn-ghost" onClick={() => setSwitchAsk(false)} disabled={switchBusy}>Cancel</button>
            <button className="btn btn-ghost" onClick={() => { setSwitchAsk(false); doSwitch(); }} disabled={switchBusy}>Switch without saving</button>
            <button className="btn btn-primary" onClick={saveThenSwitch} disabled={switchBusy}>{switchBusy ? <span className="spin" /> : "Save & switch"}</button>
          </>}>
          <p style={{ margin: 0, lineHeight: 1.6 }}>You're in the middle of a record. Do you want to save your work before switching your client or process?</p>
        </Modal>
      )}
      {navAsk && (
        <Modal title="Unsaved changes" sub="You've entered changes on this record"
          onClose={() => setNavAsk(null)}
          foot={<>
            <button className="btn btn-ghost" onClick={() => setNavAsk(null)} disabled={navBusy}>Cancel</button>
            <button className="btn btn-ghost" onClick={() => doNav(navAsk)} disabled={navBusy}>Switch without saving</button>
            <button className="btn btn-primary" onClick={saveThenNav} disabled={navBusy}>{navBusy ? <span className="spin" /> : "Save & close"}</button>
          </>}>
          <p style={{ margin: 0, lineHeight: 1.6 }}>Save your changes before leaving, or switch without saving?</p>
        </Modal>
      )}
      {faqOpen && <FaqViewer onClose={() => setFaqOpen(false)} />}
      <UnreadPosts />
      <ToastHost toasts={toasts} />
    </ToastCtx.Provider>
  );
}